
import { flow, isEmpty, map, omit, omitBy } from 'lodash-es'
import { Component, Prop, Mixins } from 'vue-property-decorator'
import { deserialize } from '@shared/jsona'
import ProviderStatisticsFeature from '../features/ProviderStatistics'
import AvatarBasic from '~/components/common/AvatarBasic.vue'
import Icon from '~/components/common/icon/Icon.vue'
import { getCredibilityScoreValue } from '~/lib/utils/utils'
import fillPropRule from '~/plugins/mixpanel/values'
import { DeserializedScholarship, ProviderStats } from '~/types/jsonapi/scholarship'
import { PROVIDER_STATISTICS_LOADED } from '~/plugins/mixpanel/events'
import { SCHOLARSHIP_ID, SCHOLARSHIP_PROVIDER_NAME, SCHOLARSHIP_PROVIDER_STATISTICS, SCHOLARSHIP_PROVIDER_IS_VISIBLE } from '~/plugins/mixpanel/props'
import BaliTitle from '~/components/common/BaliTitle.vue'

export const SocialNetwork = {
  x: 'X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  instagram: 'Instagram'
} as const

export interface SocialNetworkUrls
  extends Record<keyof typeof SocialNetwork, string> {}

interface BrandingIdentity extends SocialNetworkUrls {
  logo?: string
  mission?: string
}

@Component({
  components: {
    Icon,
    AvatarBasic,
    BaliTitle
  }
})
export default class ProviderDescription extends Mixins(ProviderStatisticsFeature) {
  @Prop({ required: true }) readonly scholarshipId: string
  @Prop({ default: null }) readonly brandIdentity: BrandingIdentity
  @Prop({ default: '' }) readonly providerName: string
  @Prop({ default: '' }) readonly providerLink: string
  @Prop({ default: '' }) readonly tabName: string
  @Prop({ default: false }) readonly sm: boolean
  @Prop() readonly confidenceScore: any

  private isOpen = false
  private providerStats: ProviderStats = null

  async created() {
    await this.$api.scholarship.providerStats({
      id: this.scholarshipId
    }).then((res) => {
      this.providerStats = deserialize<DeserializedScholarship>(
        res.rawRes
      ).meta?.providerStats

      if (this.providerStats) {
        this.$mixpanel.track(PROVIDER_STATISTICS_LOADED, {
          [SCHOLARSHIP_ID]: this.scholarshipId,
          [SCHOLARSHIP_PROVIDER_NAME]: this.providerName,
          [SCHOLARSHIP_PROVIDER_STATISTICS]: { ...this.providerStats }, // POJO instead of an observable
          [SCHOLARSHIP_PROVIDER_IS_VISIBLE]: this.getProviderStatisticsEnabled()
        })
      }
    })
  }

  get text() {
    return this.brandIdentity?.mission
  }

  get truncatedText(): string {
    if (!this.text) {
      return ''
    }

    if (this.sm && this.text.length > 140) {
      return this.text.substring(0, 170) + '...'
    }

    if (!this.sm && this.text.length > 250) {
      return this.text.substring(0, 250) + '...'
    }

    return ''
  }

  get finalText(): string {
    return !this.truncatedText.length
      ? this.text
      : this.isOpen
        ? this.text
        : this.truncatedText
  }

  triggerProviderURLVisitEvent() {
    this.$mixpanel.track(this.$mixpanel.events.PROVIDER_URL_VISIT, {
      'Maintenance mode': this.$accessor.account.isMaintenanceMode,
      [this.$mixpanel.props.CREDIBILITY_SCORE]: getCredibilityScoreValue(
        Number(this.confidenceScore)
      )
    })
  }

  trackSocialClick(socialName: keyof typeof SocialNetwork) {
    const { track, events, fillProps, props } = this.$mixpanel

    track(
      events.PROVIDER_SOCIAL_LINK_CLICK,
      fillProps(events.PROVIDER_SOCIAL_LINK_CLICK, {
        scholarshipId: Number(this.scholarshipId),
        props: {
          [props.SCHOLARSHIP_PROVIDER_NAME]: this.providerName,
          [props.SOCIAL_NETWORK_NAME]:
            fillPropRule[props.SOCIAL_NETWORK_NAME](socialName)
        }
      })
    )
  }

  trackShowMoreClick() {
    const { track, events, fillProps, props } = this.$mixpanel

    if (!this.isOpen) {
      return
    }

    const event = events.PROVIDER_MISSION_SHOW_MORE_CLICK

    track(
      event,
      fillProps(event, {
        scholarshipId: Number(this.scholarshipId),
        props: {
          [props.SCHOLARSHIP_PROVIDER_NAME]: this.providerName
        }
      })
    )
  }

  get logo() {
    const src = this.brandIdentity?.logo
    const { providerName } = this
    return src
      ? {
          src,
          alt: `${providerName} logo`
        }
      : undefined
  }

  get socials() {
    return flow(
      () => this.brandIdentity,
      value => omit(value, ['logo', 'mission']),
      value => omitBy(value, isEmpty),
      value =>
        map(value, (link: string, name: keyof typeof SocialNetwork) => ({
          name,
          link
        }))
    )()
  }

  get isProviderDescriptionVisible() {
    return this.brandIdentity || (this.providerStats && this.getProviderStatisticsEnabled())
  }

  get providerStatsForDisplay() {
    const { scholarshipsAmount, totalAmountWon } = this.providerStats

    if (totalAmountWon === 0 || scholarshipsAmount === 1) {
      return [
        {
          label: 'Recently joined',
          value: '🎉'
        }
      ]
    }

    return [
      {
        label: 'Total scholarships:',
        value: scholarshipsAmount,
        tooltip: {
          content: 'This includes both past and current scholarships'
        }
      },
      {
        label: 'Total amount awarded:',
        value: `$${totalAmountWon.toLocaleString('en-US')}`
      }
    ]
  }
}
